import { Box, Divider, Icon, Typography } from "@outschool/backpack";
import { ClassDetailsQuery_ActivityFragment } from "@outschool/gql-frontend-generated";
import {
  IconDefinition,
  faBackpack,
  faBooksMedical,
  faFile,
  faGlasses,
  faHandPointRibbon,
  faLanguage,
  faListCheck
} from "@outschool/icons";
import {
  TFunction,
  Trans,
  useMachineTranslation,
  useTranslation
} from "@outschool/localization";
import { useLanguageString } from "@outschool/ui-components-website";
import { useImpressionTracking } from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import React, { useRef } from "react";

import { englishProficiencyLevelOptions } from "../subcomponents/ActivityAttributes";
import HeadingMarker from "../subcomponents/HeadingMarker";
import { YellowBlobIcon } from "../subcomponents/IconWithBackground";
import ShowMoreModalControlled from "../subcomponents/ShowMoreModalControlled";

export default function OtherDetails({
  activity,
  headerRef,
  showExpanded
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  headerRef?: React.RefObject<HTMLElement>;
  showExpanded?: boolean;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\sections\\OtherDetails"
  );

  const { translate } = useMachineTranslation();
  const detailsArray = buildDetailsArray(activity, t, translate);
  const detailsCount = detailsArray.length;

  const [otherDetailsModalOpen, setOtherDetailsModalOpen] =
    React.useState(false);
  const showMoreButtonRef = useRef(null);

  const { trackTouch: trackShowMoreTouch } = useImpressionTracking({
    node: showMoreButtonRef.current,
    uniqueId: "show_more_other_details",
    trackingLabel: "show_more_other_details"
  });

  const ExpandedDetails = React.useMemo(() => {
    return detailsArray.map(([title, text], index) => {
      const icon = getIconByTitle(title, t);
      return (
        <DetailModalRow
          key={index}
          title={title}
          text={text}
          icon={icon}
          isLast={index === detailsArray.length - 1}
          activity={activity}
          t={t}
        />
      );
    });
  }, [detailsArray, t, activity]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 8
      }}
    >
      <Box ref={headerRef}>
        <HeadingMarker />
        <Typography variant="h2" sx={{ fontWeight: 700 }}>
          {t("Other Details")}
        </Typography>
      </Box>

      {showExpanded ? (
        ExpandedDetails
      ) : (
        <>
          {detailsArray.map(([title, text], index) => {
            const icon = getIconByTitle(title, t);
            return (
              <DetailRow
                key={index}
                title={title}
                text={text}
                icon={icon}
                activity={activity}
                onClicked={() => {
                  trackShowMoreTouch();
                  setOtherDetailsModalOpen(true);
                }}
                t={t}
              />
            );
          })}
          {!showExpanded && (
            <ShowMoreModalControlled
              ref={showMoreButtonRef}
              isModalOpen={otherDetailsModalOpen}
              onButtonClicked={() => {
                trackShowMoreTouch();
                setOtherDetailsModalOpen(true);
              }}
              onCloseModal={() => setOtherDetailsModalOpen(false)}
              buttonLabel={t("Show More", {
                count: detailsCount,
                defaultValue_plural: "Show All {{count}} Details"
              })}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 16 }}>
                {t("Other Details")}
              </Typography>
              {ExpandedDetails}
            </ShowMoreModalControlled>
          )}
        </>
      )}
    </Box>
  );
}

function DetailRow({
  title,
  text,
  icon,
  activity,
  onClicked,
  t
}: {
  title: string;
  text: string;
  icon: IconDefinition;
  activity: ClassDetailsQuery_ActivityFragment;
  onClicked?: () => void;
  t: TFunction;
}) {
  const isMobile = Screen.useIsMobile();
  const { codeToTranslatedText } = useLanguageString();

  const renderContent = () => {
    if (title === t("Language of Instruction")) {
      const englishLevel = activity.english_proficiency_level
        ? englishProficiencyLevelOptions.find(
            option => option.level === activity.english_proficiency_level
          )
        : null;

      const levelText = englishLevel
        ? t(" (Level: {{label}})", {
            label: englishLevel.label
          })
        : "";
      return `${codeToTranslatedText(
        activity.languageOfInstruction
      )}${levelText}`;
    } else if (
      title === t("External Resources") ||
      title === t("Supply List")
    ) {
      return (
        <ContentRenderer title={title} text={text} activity={activity} t={t} />
      );
    }

    return text;
  };

  return (
    <Box
      onClick={onClicked}
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "flex-start",
        borderRadius: 16,
        padding: 16,
        gap: isMobile ? 8 : 16,
        backgroundColor: "grey.50",
        width: "100%",
        flexWrap: "wrap",
        cursor: onClicked ? "pointer" : "default",
        "&:hover": {
          backgroundColor: onClicked ? "grey.100" : "grey.50"
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 8,
          alignItems: "center",
          flex: 1
        }}
      >
        <YellowBlobIcon icon={icon} />
        <Typography variant="subtitle1" sx={{ whiteSpace: "nowrap" }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 2, marginTop: isMobile ? 4 : 0 }}>
        <Typography
          variant="body1"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical"
          }}
        >
          {renderContent() as string}
        </Typography>
      </Box>
    </Box>
  );
}

function DetailModalRow({
  title,
  text,
  icon,
  extraInfo,
  isLast,
  activity,
  t
}: {
  title: string;
  text: string;
  icon: IconDefinition;
  extraInfo?: React.ReactNode;
  isLast: boolean;
  activity: ClassDetailsQuery_ActivityFragment;
  t: TFunction;
}) {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: ".8rem",
          marginTop: "1em",
          marginBottom: "1em",
          flexWrap: "wrap"
        }}
      >
        <YellowBlobIcon icon={icon} />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{ whiteSpace: "nowrap", marginBottom: 8 }}
          >
            <Trans>{title}</Trans>
          </Typography>
          <ContentRenderer
            title={title}
            text={text}
            activity={activity}
            t={t}
          />
          {extraInfo}
        </Box>
      </Box>
      {!isLast && <Divider sx={{ borderColor: "grey.300" }} />}
    </>
  );
}

function ContentRenderer({
  title,
  text,
  activity,
  t
}: {
  title: string;
  text: string;
  activity: ClassDetailsQuery_ActivityFragment;
  t: TFunction;
}) {
  const isMobile = Screen.useIsMobile();
  const { codeToTranslatedText } = useLanguageString();

  if (title === t("Language of Instruction")) {
    const languageText = t("This class is taught in {{language}}.", {
      language: codeToTranslatedText(activity.languageOfInstruction)
    });

    const minimumEnglishLevel = activity.english_proficiency_level
      ? (() => {
          const option = englishProficiencyLevelOptions.find(
            option => option.level === activity.english_proficiency_level
          );
          if (option) {
            return (
              <Box
                sx={{
                  marginTop: 8,
                  padding: 12,
                  backgroundColor: "grey.50",
                  borderRadius: 8,
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <Typography variant="subtitle1" sx={{ marginBottom: 4 }}>
                  {t("Minimum required English level:")}
                </Typography>
                <Typography variant="body1">
                  <Trans>
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      {t("{{label}}", {
                        label: option.label
                      })}
                    </Box>
                    <Box component="span">
                      {t("- {{description}}", {
                        description: option.description
                      })}
                    </Box>
                  </Trans>
                </Typography>
              </Box>
            );
          }
          return null;
        })()
      : null;

    return (
      <Box sx={{ flex: 2, marginTop: isMobile ? 4 : 0 }}>
        <Typography variant="body1">
          <Trans>{languageText}</Trans>
        </Typography>
        {minimumEnglishLevel}
      </Box>
    );
  }

  if (title === t("External Resources")) {
    return activity.has_external_resources ? (
      <Box sx={{ flex: 2, marginTop: isMobile ? 4 : 0 }}>
        <Typography variant="body1" sx={{ marginBottom: 1 }}>
          {t("In addition to the Outschool classroom, this class uses:")}
        </Typography>
        <Box component="ul" sx={{ paddingLeft: 20, margin: 0 }}>
          {activity.externalResources?.map(
            ({
              uid,
              name,
              url
            }: {
              uid: string | null;
              name: string | null;
              url: string | null;
            }) => (
              <Box component="li" key={uid} sx={{ marginBottom: 1 }}>
                {url ? (
                  <a
                    href={appendProtocol(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "black", textDecoration: "none" }}
                  >
                    {name ? name : url}
                  </a>
                ) : (
                  name!
                )}
              </Box>
            )
          )}
        </Box>
      </Box>
    ) : (
      <Box sx={{ flex: 2, marginTop: isMobile ? 4 : 0 }}>
        {t(
          "Learners will not need to use any apps or websites beyond the standard Outschool tools."
        )}
      </Box>
    );
  }

  if (title === t("Supply List")) {
    return activity.materials ? (
      <Box sx={{ flex: 2, marginTop: 0 }}>
        <Typography
          component="pre"
          variant="body1"
          sx={{
            display: "block",
            flex: 2,
            marginTop: isMobile ? 4 : 0
          }}
        >
          {text}
        </Typography>
        {activity.welcomeAttachmentCount &&
        activity.welcomeAttachmentCount > 0 ? (
          <Box sx={{ marginTop: 2 }}>
            <Typography
              variant="body1"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Icon icon={faFile} /> &nbsp;
              {t("{{numFiles}} file available upon enrollment", {
                numFiles: activity.welcomeAttachmentCount,
                count: activity.welcomeAttachmentCount,
                defaultValue_plural: `{{numFiles}} files available upon enrollment`
              })}
            </Typography>
          </Box>
        ) : null}
      </Box>
    ) : null;
  }

  return (
    <Typography
      component="pre"
      variant="body1"
      sx={{
        display: "block",
        flex: 2,
        marginTop: isMobile ? 4 : 0
      }}
    >
      {text}
    </Typography>
  );
}

function buildDetailsArray(
  activity: ClassDetailsQuery_ActivityFragment,
  t: TFunction,
  translate: any
) {
  const detailsArray: [string, string][] = [];

  const fields = [
    { key: "learning_needs", label: t("Learning Needs") },
    { key: "parental_guidance", label: t("Parental Guidance") },
    { key: "prerequisites", label: t("Pre-Requisites") },
    { key: "materials", label: t("Supply List") },
    { key: "languageOfInstruction", label: t("Language of Instruction") },
    { key: "externalResources", label: t("External Resources") },
    { key: "clubGuidelines", label: t("Guidelines") },
    { key: "sources", label: t("Sources") }
  ];

  fields.forEach(field => {
    let item;

    if (field.key === "learning_needs") {
      item =
        activity.attributes?.find((a: any) => a.key === "learning_needs")
          ?.value || "";
    } else if (field.key === "externalResources") {
      item =
        activity.has_external_resources &&
        activity.externalResources?.some((r: { name: any }) => r?.name)
          ? activity.externalResources
              .filter((r: { name: any }) => r?.name)
              .map((r: any) => r!.name)
              .join(", ")
          : t(
              "Learners will not need to use any apps or websites beyond the standard Outschool tools."
            );
    } else if (field.key === "prerequisites") {
      item =
        activity.attributes?.find((a: any) => a.key === "pre_reqs")?.value ||
        "";
    } else if (field.key === "clubGuidelines") {
      item = activity.clubGuidelines?.text || "";
    } else {
      item = activity[field.key as keyof ClassDetailsQuery_ActivityFragment];
    }

    if (item) {
      const translatedItem = translate(item.trim());
      detailsArray.push([field.label, translatedItem as string]);
    }
  });

  return detailsArray;
}

function getIconByTitle(title: string, t: TFunction) {
  switch (title) {
    case t("Learning Needs"):
      return faGlasses;
    case t("Parental Guidance"):
      return faHandPointRibbon;
    case t("Pre-Requisites"):
      return faListCheck;
    case t("External Resources"):
      return faBooksMedical;
    case t("Supply List"):
      return faBackpack;
    case t("Language of Instruction"):
      return faLanguage;
    case t("Sources"):
      return faListCheck;
    case t("Guidelines"):
      return faListCheck;
    default:
      return faListCheck;
  }
}

function appendProtocol(url: string) {
  if (!/^https?:\/\//i.test(url)) {
    return "http://" + url;
  }
  return url;
}
